/**
 * Pure helpers for the payment-method UI.
 *
 * Centralizing these here keeps the React components pure-ish and easy to
 * test, and prevents the same `if (isExpired) ... else if (isPastDue) ...`
 * logic from being copy-pasted around the dashboard.
 *
 * Health states (mirrors the design):
 *   - "healthy"          : nothing to flag, render normally
 *   - "expiring_soon"    : default card expires within ~30 days, soft warning
 *   - "expired"          : default card already expired (will fail next renewal)
 *   - "past_due"         : Stripe says the active subscription is past_due
 *   - "missing"          : there is an active paid sub but no PM on file
 *   - "no_paid"          : no paid Stripe subscription (free or grant-only)
 */

export const PAYMENT_HEALTH_STATES = Object.freeze({
  HEALTHY: "healthy",
  EXPIRING_SOON: "expiring_soon",
  EXPIRED: "expired",
  PAST_DUE: "past_due",
  MISSING: "missing",
  NO_PAID: "no_paid",
});

/**
 * Decide whether to render the section above plans, below them, or not at all.
 *
 *   "above"  → user has a paid subscription; this is a primary action area.
 *   "below"  → user is free (or grant-only) but has saved payment methods
 *              they may want to manage.
 *   "hidden" → free user with nothing on file; no need to clutter the page.
 */
export const PAYMENT_SECTION_PLACEMENTS = Object.freeze({
  ABOVE: "above",
  BELOW: "below",
  HIDDEN: "hidden",
});

const _toInt = (value) => {
  const n = Number(value);
  return Number.isFinite(n) ? Math.trunc(n) : null;
};

/**
 * Compare (year, month) pairs without crossing year boundaries naively.
 * Returns -1 if a < b, 0 if equal, 1 if a > b.
 */
const _compareYearMonth = (yA, mA, yB, mB) => {
  if (yA !== yB) return yA < yB ? -1 : 1;
  if (mA !== mB) return mA < mB ? -1 : 1;
  return 0;
};

/**
 * Returns true if the (exp_month, exp_year) pair is on or before the given month.
 * `now` defaults to the current date — passed in as a parameter for testability.
 */
export const isCardExpired = (expMonth, expYear, { now = new Date() } = {}) => {
  const m = _toInt(expMonth);
  const y = _toInt(expYear);
  if (!m || !y || m < 1 || m > 12 || y <= 0) return false;
  const nowYear = now.getFullYear();
  // getMonth() is 0-indexed; we want 1-indexed for comparison.
  const nowMonth = now.getMonth() + 1;
  return _compareYearMonth(y, m, nowYear, nowMonth) < 0;
};

/**
 * Returns true if the card's expiry **month begins** within the next
 * `daysAhead` days (default 30) but the card is NOT already expired.
 *
 * Semantics: a card that reads "06/26" stays valid through end of June 2026.
 * We start warning the user ~30 days before that expiry month begins so they
 * have time to update before any renewal fails.
 *
 * Implementation note: JS `Date.UTC(year, monthIndex, day)` uses a 0-indexed
 * month (0 = January). Our `expMonth` is 1-indexed, so `monthIndex = m - 1`.
 */
export const isCardExpiringSoon = (
  expMonth,
  expYear,
  { now = new Date(), daysAhead = 30 } = {}
) => {
  const m = _toInt(expMonth);
  const y = _toInt(expYear);
  if (!m || !y || m < 1 || m > 12 || y <= 0) return false;
  if (isCardExpired(m, y, { now })) return false;

  const expiryMonthStart = new Date(Date.UTC(y, m - 1, 1, 0, 0, 0));
  const cutoff = new Date(now.getTime() + daysAhead * 24 * 60 * 60 * 1000);
  return expiryMonthStart <= cutoff;
};

/**
 * Derive the single health state we render off.
 *
 * @param {object} input
 * @param {object|null} input.paymentMethod - default_payment_method payload
 * @param {boolean} input.hasActivePaidSubscription
 * @param {string}  input.subscriptionStatus - raw Stripe status (e.g., "past_due")
 * @param {Date}    [input.now] - injected for tests
 */
export const derivePaymentHealth = ({
  paymentMethod,
  hasActivePaidSubscription,
  subscriptionStatus,
  now = new Date(),
} = {}) => {
  const status = String(subscriptionStatus || "").toLowerCase();
  if (status === "past_due" || status === "suspended") {
    return PAYMENT_HEALTH_STATES.PAST_DUE;
  }
  if (hasActivePaidSubscription && !paymentMethod) {
    return PAYMENT_HEALTH_STATES.MISSING;
  }
  if (paymentMethod) {
    if (
      paymentMethod.is_expired ||
      isCardExpired(paymentMethod.exp_month, paymentMethod.exp_year, { now })
    ) {
      return PAYMENT_HEALTH_STATES.EXPIRED;
    }
    if (isCardExpiringSoon(paymentMethod.exp_month, paymentMethod.exp_year, { now })) {
      return PAYMENT_HEALTH_STATES.EXPIRING_SOON;
    }
    return PAYMENT_HEALTH_STATES.HEALTHY;
  }
  return PAYMENT_HEALTH_STATES.NO_PAID;
};

/**
 * Decide where the section should render relative to the plans list.
 *
 * @param {object} input
 * @param {boolean} input.hasStripeCustomer
 * @param {boolean} input.hasActivePaidSubscription
 * @param {Array}   input.savedPaymentMethods
 */
export const derivePaymentSectionPlacement = ({
  hasStripeCustomer,
  hasActivePaidSubscription,
  savedPaymentMethods,
} = {}) => {
  if (hasActivePaidSubscription) return PAYMENT_SECTION_PLACEMENTS.ABOVE;
  const saved = Array.isArray(savedPaymentMethods) ? savedPaymentMethods : [];
  if (hasStripeCustomer && saved.length > 0) return PAYMENT_SECTION_PLACEMENTS.BELOW;
  return PAYMENT_SECTION_PLACEMENTS.HIDDEN;
};

/**
 * Pretty-print a card (e.g., "Visa •••• 4242").
 * Falls back to "Card on file" if brand/last4 are missing.
 */
export const formatCardSummary = (paymentMethod) => {
  if (!paymentMethod) return null;
  const brand = String(paymentMethod.brand || "").trim();
  const last4 = String(paymentMethod.last_four || "").trim();
  if (!brand && !last4) return "Card on file";
  const brandLabel = brand
    ? brand.charAt(0).toUpperCase() + brand.slice(1)
    : "Card";
  if (!last4) return brandLabel;
  return `${brandLabel} •••• ${last4}`;
};

const _padTwo = (n) => String(n).padStart(2, "0");

/**
 * Format expiration as "MM/YY" or null when unknown.
 */
export const formatCardExpiry = (paymentMethod) => {
  if (!paymentMethod) return null;
  const m = _toInt(paymentMethod.exp_month);
  const y = _toInt(paymentMethod.exp_year);
  if (!m || !y) return null;
  return `${_padTwo(m)}/${String(y).slice(-2)}`;
};

/**
 * Format billing address as a single short line (country + postal_code).
 * Returns null if the address has no country.
 */
export const formatBillingAddressShort = (billingAddress) => {
  if (!billingAddress) return null;
  const country = String(billingAddress.country || "").trim().toUpperCase();
  if (!country) return null;
  const postal = String(billingAddress.postal_code || "").trim();
  return postal ? `${country} · ${postal}` : country;
};
